import React, { useCallback, useMemo } from 'react';
import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

const COLUMNS: DataTableColumn[] = [
  { key: 'date', label: 'Date', width: '100px' },
  { key: 'shiftId', label: 'Shift ID', noWrap: true },
  { key: 'location', label: 'Location' },
  { key: 'position', label: 'Position' },
  { key: 'timeframe', label: 'Timeframe' },
  { key: 'cancelledBy', label: 'Cancelled By', noWrap: true },
  { key: 'teamMember', label: 'Team Member' },
  { key: 'hoursBeforeStart', label: 'Hours Before Start', align: 'center', noWrap: true },
];

export const LastMinuteCancellationsTable: React.FC = () => {
  const data = useFulfillmentDashboardStore((s) => s.lastMinuteCancellationDetails);
  const currentPage = useFulfillmentDashboardStore((s) => s.lastMinuteCancellationPage);
  const totalPages = useFulfillmentDashboardStore((s) => s.lastMinuteCancellationTotalPages);
  const isLoading = useFulfillmentDashboardStore((s) => s.lastMinuteCancellationLoading);
  const hasError = useFulfillmentDashboardStore((s) => s.lastMinuteCancellationError);
  const fetchLastMinuteCancellations = useFulfillmentDashboardStore(
    (s) => s.fetchLastMinuteCancellations,
  );

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchLastMinuteCancellations(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchLastMinuteCancellations, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        date: d.date,
        shiftId: d.shiftId,
        location: d.location,
        position: d.position,
        timeframe: d.timeframe,
        cancelledBy: d.cancelledBy,
        teamMember: d.teamMember,
        hoursBeforeStart: d.hoursBeforeStart,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load last-minute cancellations data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Last-minute cancellations detail table"
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
